import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { Building2, Users, ShieldCheck, FileText, BadgeDollarSign, Loader2, Download, Activity, Bell, Home, UserPlus, Ban, Upload } from "lucide-react";
import { toast, Toaster } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

function MetricTile({ label, value, icon: Icon, tone = "baobab" }) {
  const toneCls = tone === "terracotta"
    ? "bg-[#c15337]/8 text-[#8b3a23] border-[#c15337]/20"
    : tone === "sand"
    ? "bg-[#d49a36]/12 text-[#8a5a16] border-[#d49a36]/30"
    : "bg-[#114232]/6 text-[#114232] border-[#114232]/15";
  return (
    <div className={`p-5 rounded-md border ${toneCls}`} data-testid={`partner-metric-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-bold opacity-70">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="text-3xl font-heading font-bold mt-1.5">{value}</div>
    </div>
  );
}

export default function PartnerAdmin() {
  const { user, logout } = useAuth();
  const [dash, setDash] = useState(null);
  const [merchants, setMerchants] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    email: "", name: "", password: "", role: "partner_viewer",
  });
  const [inviteSaving, setInviteSaving] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);

  const canManageTeam = user?.role === "partner_admin" || user?.role === "super_admin";

  async function reloadStaff() {
    try {
      const r = await api.get("/partner/staff");
      setStaff(r.data?.items || []);
    } catch (e) {
      // 403 if not partner role — silently ignore
      if (e.response?.status !== 403) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    }
  }

  useEffect(() => {
    Promise.all([
      api.get("/partner/dashboard"),
      api.get("/partner/merchants?limit=50"),
      api.get("/partner/revenue"),
      api.get("/partner/notifications?limit=8"),
      api.get("/partner/audit-logs?limit=10"),
      api.get("/partner/staff").catch(() => ({ data: { items: [] } })),
    ])
      .then(([d, m, rev, n, a, s]) => {
        setDash(d.data);
        setMerchants(m.data?.items || []);
        setRevenue(rev.data);
        setNotifications(n.data?.items || []);
        setAuditLogs(a.data?.items || []);
        setStaff(s.data?.items || []);
      })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, []);

  async function submitInvite(e) {
    e.preventDefault();
    setInviteSaving(true);
    try {
      await api.post("/partner/staff", inviteForm);
      toast.success("Staff member invited");
      setInviteForm({ email: "", name: "", password: "", role: "partner_viewer" });
      setInviteOpen(false);
      reloadStaff();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setInviteSaving(false);
    }
  }

  async function confirmRemove() {
    if (!removeTarget) return;
    try {
      await api.delete(`/partner/staff/${removeTarget.id}`);
      toast.success(`Removed ${removeTarget.email}`);
      setRemoveTarget(null);
      reloadStaff();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  async function handleCsvUpload(file) {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/partner/staff/bulk-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${r.data.created_count} · skipped ${r.data.skipped_count}`);
      if (r.data.skipped_count > 0) {
        const reasons = r.data.skipped.slice(0, 3).map((s) => `row ${s.row}: ${s.reason}`).join("\n");
        toast.warning(`Some rows skipped:\n${reasons}${r.data.skipped.length > 3 ? "\n…" : ""}`);
      }
      reloadStaff();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  async function downloadStatement() {
    try {
      const { data: blob } = await api.get(
        "/partner/revenue/statement.pdf", { responseType: "blob" },
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `partner_statement_${revenue?.period?.label?.replace(" ", "_") || "current"}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  // Hard redirect non-partner-admins to their own home
  if (user && !["partner_admin", "partner_analyst", "partner_viewer", "super_admin"].includes(user.role)) {
    return <Navigate to={(user.role === "merchant" || user.role === "merchant_staff") ? "/merchant" : "/app"} replace />;
  }

  if (loading || !dash) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f4f2eb]" data-testid="partner-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#114232]" />
      </div>
    );
  }

  const p = dash.partner || {};
  const m = dash.metrics || {};
  const primary = p.primary_color || "#114232";

  return (
    <div className="min-h-screen bg-[#f4f2eb]" data-testid="partner-admin-page">
      <Toaster position="top-right" />
      <header className="py-6 px-8 border-b border-[#114232]/10 bg-white">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            {p.logo_url ? (
              <img src={p.logo_url} alt={p.name} className="h-10 w-auto" />
            ) : (
              <div className="w-10 h-10 rounded-md flex items-center justify-center text-white"
                    style={{ backgroundColor: primary }}>
                <Building2 className="w-5 h-5" />
              </div>
            )}
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: p.accent_color || "#c15337" }}>
                Partner Portal
              </div>
              <h1 className="font-heading text-2xl font-bold" style={{ color: primary }} data-testid="partner-name">
                {p.name}
              </h1>
            </div>
          </div>
          <Link
            to="/"
            data-testid="partner-view-landing-link"
            title="Preview the public landing page"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-[#114232]/20 rounded-md hover:bg-[#114232]/5"
          >
            <Home className="w-4 h-4" />
            View landing
          </Link>
          <button
            onClick={logout}
            data-testid="partner-logout-btn"
            className="px-4 py-2 text-sm font-medium border border-[#114232]/20 rounded-md hover:bg-[#114232]/5"
          >
            Sign out
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-8 py-8 space-y-8">
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="partner-metrics">
          <MetricTile label="Merchants" value={m.merchant_count?.toLocaleString() || 0} icon={Users} />
          <MetricTile label="Active policies" value={m.active_policies?.toLocaleString() || 0} icon={ShieldCheck} tone="sand" />
          <MetricTile label="Open claims" value={m.open_claims?.toLocaleString() || 0} icon={FileText} tone="terracotta" />
          <MetricTile label="Paid claims" value={m.paid_claims?.toLocaleString() || 0} icon={BadgeDollarSign} />
        </section>

        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading text-lg font-bold text-[#114232]">Merchants under {p.name}</h2>
            <span className="text-xs text-[#5c6360] font-mono">{merchants.length} shown</span>
          </div>
          <div className="bg-white border border-[#114232]/10 rounded-md overflow-x-auto">
            <table className="w-full text-sm" data-testid="partner-merchants-table">
              <thead className="text-[11px] uppercase tracking-wider text-stone-500">
                <tr>
                  <th className="text-left font-medium py-3 px-5">Business</th>
                  <th className="text-left font-medium py-3 px-5">Owner</th>
                  <th className="text-left font-medium py-3 px-5">Phone</th>
                  <th className="text-left font-medium py-3 px-5">KYC</th>
                  <th className="text-left font-medium py-3 px-5">Status</th>
                  <th className="text-right font-medium py-3 px-5">Score</th>
                </tr>
              </thead>
              <tbody>
                {merchants.map((mt) => (
                  <tr key={mt.id} className="border-t border-[#114232]/10" data-testid={`partner-merchant-${mt.id}`}>
                    <td className="py-3 px-5 font-medium text-[#114232]">{mt.business_name}</td>
                    <td className="py-3 px-5">{mt.owner_name}</td>
                    <td className="py-3 px-5 font-mono text-xs">{mt.phone}</td>
                    <td className="py-3 px-5 text-xs">
                      <span className={`px-2 py-0.5 rounded font-semibold uppercase tracking-wider ${
                        mt.kyc_status === "verified" ? "bg-[#114232]/8 text-[#114232]" :
                        mt.kyc_status === "rejected" ? "bg-[#c15337]/12 text-[#8b3a23]" :
                        "bg-[#d49a36]/15 text-[#8a5a16]"
                      }`}>
                        {(mt.kyc_status || "—").replace(/_/g, " ")}
                      </span>
                    </td>
                    <td className="py-3 px-5 text-xs capitalize">{mt.status}</td>
                    <td className="py-3 px-5 text-right font-mono">{mt.risk_score}</td>
                  </tr>
                ))}
                {merchants.length === 0 && (
                  <tr><td colSpan={6} className="py-10 text-center text-[#5c6360]">No merchants under this partner yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* Revenue & commission */}
        {revenue && (
          <section data-testid="partner-revenue-section">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div>
                <h2 className="font-heading text-lg font-bold text-[#114232]">Revenue & commission</h2>
                <div className="text-xs text-[#5c6360] mt-0.5">
                  {revenue.period.label} · commission rate <span className="font-mono">{(revenue.partner.commission_rate_bps / 100).toFixed(2)}%</span>
                </div>
              </div>
              <button
                type="button"
                onClick={downloadStatement}
                data-testid="partner-statement-pdf-btn"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] uppercase tracking-wider font-semibold border border-[#114232]/15 rounded-md text-[#114232] hover:bg-[#114232]/5"
              >
                <Download className="w-3.5 h-3.5" /> Download statement
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MetricTile label="Premium collected" value={`KES ${revenue.metrics.total_premium_kes.toLocaleString()}`} icon={BadgeDollarSign} />
              <MetricTile label="Claims paid" value={`KES ${revenue.metrics.total_payouts_kes.toLocaleString()}`} icon={FileText} tone="terracotta" />
              <MetricTile label="Your commission" value={`KES ${revenue.metrics.commission_kes.toLocaleString()}`} icon={ShieldCheck} tone="sand" />
              <MetricTile label="Net to underwriter" value={`KES ${revenue.metrics.net_to_underwriter_kes.toLocaleString()}`} icon={Users} />
            </div>
          </section>
        )}

        {/* Notifications + Audit log */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="partner-ops-section">
          <div className="bg-white border border-[#114232]/10 rounded-md p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-[#c15337]" />
                <h3 className="font-heading text-base font-bold text-[#114232]">Recent notifications</h3>
              </div>
              <span className="text-[10px] uppercase tracking-wider text-[#5c6360] font-mono">{notifications.length}</span>
            </div>
            <ul className="space-y-2" data-testid="partner-notifications-list">
              {notifications.map((n) => (
                <li key={n.id} className="border border-[#114232]/8 rounded p-2.5" data-testid={`partner-notification-${n.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-[#c15337]">{n.kind?.replace(/_/g, " ")}</span>
                    <span className="text-[10px] font-mono text-[#5c6360]">{n.created_at?.slice(0, 16).replace("T", " ")}</span>
                  </div>
                  <div className="text-sm font-medium text-[#114232] mt-1">{n.title}</div>
                  {n.body && <div className="text-xs text-[#5c6360] mt-0.5 line-clamp-2">{n.body}</div>}
                </li>
              ))}
              {notifications.length === 0 && (
                <li className="text-xs text-[#5c6360] py-3 text-center">No notifications yet.</li>
              )}
            </ul>
          </div>

          <div className="bg-white border border-[#114232]/10 rounded-md p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-[#114232]" />
                <h3 className="font-heading text-base font-bold text-[#114232]">Audit trail</h3>
              </div>
              <span className="text-[10px] uppercase tracking-wider text-[#5c6360] font-mono">{auditLogs.length}</span>
            </div>
            <ul className="space-y-2" data-testid="partner-audit-list">
              {auditLogs.map((a) => (
                <li key={a.id} className="border border-[#114232]/8 rounded p-2.5" data-testid={`partner-audit-${a.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-[#114232]">{a.action?.replace(/_/g, " ")}</span>
                    <span className="text-[10px] font-mono text-[#5c6360]">{a.timestamp?.slice(0, 16).replace("T", " ")}</span>
                  </div>
                  <div className="text-xs text-[#5c6360] mt-1 font-mono">
                    by {a.user_email} · {a.entity_type}/{a.entity_id?.slice(0, 8)}
                  </div>
                </li>
              ))}
              {auditLogs.length === 0 && (
                <li className="text-xs text-[#5c6360] py-3 text-center">No audit entries yet.</li>
              )}
            </ul>
          </div>
        </section>
      </main>

      {/* Team management section */}
      <section className="max-w-6xl mx-auto px-8 pb-12" data-testid="partner-team-section">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <h2 className="font-heading text-lg font-bold text-[#114232]">Your team</h2>
            <div className="text-xs text-[#5c6360] mt-0.5">
              Analysts can view reports; viewers are read-only. Manage who at {p.name} can access this portal.
            </div>
          </div>
          {canManageTeam && (
            <div className="flex items-center gap-2 flex-wrap">
              <label
                className="inline-flex items-center gap-1.5 cursor-pointer text-sm px-3 py-2 rounded-md border border-[#114232]/15 hover:bg-[#114232]/5"
                data-testid="partner-bulk-csv-label"
              >
                <Upload className="w-4 h-4" /> Bulk CSV
                <input
                  type="file" accept=".csv" hidden
                  data-testid="partner-bulk-csv-input"
                  onChange={(e) => { handleCsvUpload(e.target.files?.[0]); e.target.value = ""; }}
                />
              </label>
              <Button
                onClick={() => setInviteOpen(true)}
                data-testid="partner-open-invite"
                className="bg-[#c15337] hover:bg-[#a3432a]"
              >
                <UserPlus className="w-4 h-4 mr-1.5" /> Invite teammate
              </Button>
            </div>
          )}
        </div>
        <div className="bg-white border border-[#114232]/10 rounded-md overflow-x-auto">
          <table className="w-full text-sm" data-testid="partner-team-table">
            <thead className="text-[11px] uppercase tracking-wider text-stone-500">
              <tr>
                <th className="text-left font-medium py-3 px-5">Name</th>
                <th className="text-left font-medium py-3 px-5">Email</th>
                <th className="text-left font-medium py-3 px-5">Role</th>
                <th className="text-left font-medium py-3 px-5">Status</th>
                <th className="text-right font-medium py-3 px-5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {staff.length === 0 && (
                <tr><td colSpan={5} className="py-10 text-center text-[#5c6360]" data-testid="partner-team-empty">
                  No teammates yet — invite someone to give them portal access.
                </td></tr>
              )}
              {staff.map((u) => (
                <tr key={u.id} className="border-t border-[#114232]/10"
                    data-testid={`partner-team-row-${u.id}`}>
                  <td className="py-3 px-5 font-medium text-[#114232]">{u.name}</td>
                  <td className="py-3 px-5 font-mono text-xs">{u.email}</td>
                  <td className="py-3 px-5 text-xs uppercase tracking-wider">{u.role.replace(/_/g, " ")}</td>
                  <td className="py-3 px-5 text-xs capitalize">{u.status}</td>
                  <td className="py-3 px-5 text-right">
                    {canManageTeam &&
                      (u.role === "partner_analyst" || u.role === "partner_viewer") &&
                      u.status !== "disabled" && (
                        <Button variant="ghost" size="sm"
                                  onClick={() => setRemoveTarget(u)}
                                  data-testid={`partner-team-remove-${u.id}`}
                                  className="text-[#c15337] hover:bg-[#c15337]/10">
                          <Ban className="w-3.5 h-3.5 mr-1" /> Remove
                        </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Invite modal */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent data-testid="partner-invite-dialog">
          <DialogHeader>
            <DialogTitle>Invite a teammate</DialogTitle>
            <DialogDescription>
              They'll get access scoped to {p.name} only.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitInvite} className="space-y-3" data-testid="partner-invite-form">
            <div>
              <Label>Email</Label>
              <Input type="email" required data-testid="partner-invite-email"
                value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })} />
            </div>
            <div>
              <Label>Full name</Label>
              <Input required minLength={2} data-testid="partner-invite-name"
                value={inviteForm.name} onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })} />
            </div>
            <div>
              <Label>Temporary password (min 8)</Label>
              <Input type="password" required minLength={8} data-testid="partner-invite-password"
                value={inviteForm.password} onChange={(e) => setInviteForm({ ...inviteForm, password: e.target.value })} />
            </div>
            <div>
              <Label>Role</Label>
              <Select
                value={inviteForm.role}
                onValueChange={(v) => setInviteForm({ ...inviteForm, role: v })}
              >
                <SelectTrigger data-testid="partner-invite-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="partner_viewer">Viewer (read-only)</SelectItem>
                  <SelectItem value="partner_analyst">Analyst (view + reports)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setInviteOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={inviteSaving} data-testid="partner-invite-submit">
                {inviteSaving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Send invite
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
        <AlertDialogContent data-testid="partner-remove-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this teammate?</AlertDialogTitle>
            <AlertDialogDescription>
              {removeTarget?.email} will lose portal access immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="partner-remove-confirm-btn"
              onClick={confirmRemove}
              className="bg-[#c15337] hover:bg-[#a3432a]"
            >Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
